// A v3 use case page (stories 31-43).
//
// Component tree
//   UseCaseStep (store-connected, one per selected use case)
//   └─ UseCasePageView
//        ├─ header           name, maturity tier
//        │                   record levels (CRM enrichment, TAM sourcing: Accounts / Contacts / Both) or methods
//        │                   (GTM automation: a multi-select of time-savings methods) sit right of the title
//        ├─ tool checklist   Tool consolidation only
//        ├─ How the number is built          editable cards (FormulaChain) up to the lagging result
//        ├─ Realization and attribution      result x realization = program value, x attribution = Clay-attributed
//        ├─ overlap note     when a declared overlapping pair is selected
//        └─ Summary          leading metric(s) today -> with Clay, v2's blue pulse arrow, one tile (program value + Clay-attributed)
//
// Editing state follows root CLAUDE.md "Draft State Under SSE": inputs edit a `useDraftState` text
// draft seeded once per sizing epoch + use case id, and each keystroke also pushes the parsed
// number into the sizing source (the single source of truth for the engine). Clearing an input
// restores the Clay default, so "Assumed" always means "no answer stored".
//
// The sizing source is injected (`EditorSource`): the page and the review list both read and write
// the calculator store.

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { AUTOFILL_IGNORE_PROPS } from '../../../_shared/autofill';
import { useDraftState } from '../../../_shared/hooks';
import {
  getSizedUseCase,
  type RecordLevel,
  type SizedUseCase,
} from '../../data/use-case-catalog';
import {
  computeUseCase,
  enabledLevels,
  enabledMethods,
  overlapNotesFor,
  resolveFactor,
  type DealEconomics,
  type UseCaseResult,
  type UseCaseSelection,
} from '../../engine/use-case-engine';
import { DEAL_STEP } from '../../flow/step-model';
import { numberToInput, parseNumberInput } from '../../flow/number-input';
import { NumberField } from './NumberField';
import { calculatorActions, useCalculatorStore } from '../../state/calculator-store';
import type { CustomTool } from '../../types';
import { Ph } from '../Icon';
import { FormulaChain, type AnswerEditor, type FactorEditor } from './FormulaChain';
import {
  LEVEL_CHOICES,
  LEVEL_LABEL,
  buildChain,
  buildRealizationRows,
  heroVm,
  levelChoiceOf,
  methodLetter,
  needsEntryChoice,
  questionGroups,
  summaryTileVm,
  type ChainRowVm,
  type QuestionVm,
} from './use-case-page-model';
import { UseCaseTools } from './UseCaseTools';
import { useCaseIconUrl } from './use-case-icons';

export type { AnswerEditor, FactorEditor };

/* ------------------------------------------------------------------------ pieces */

/**
 * The title block, a grid: the use case name with its notes under it on the left; the mode picker
 * (record levels / methods), when it has one, centred in the space to their right and vertically
 * centred on the title line.
 */
function Header({ uc, picker, note }: { uc: SizedUseCase; picker: ReactNode; note: ReactNode }) {
  const icon = useCaseIconUrl(uc.id);
  return (
    <header className="vc3-ucp-head">
      <div className="vc3-eyebrow">Use case</div>
      <div className="vc3-ucp-badges">
        <span className="vc3-tierbadge" data-tier={uc.tier}>
          {uc.tier}
        </span>
      </div>
      <div className="vc3-ucp-titlerow">
        <h1 className="vc3-h1 vc3-ucp-title">
          {icon && <img src={icon} alt="" />}
          {uc.name}
        </h1>
        {picker && <div className="vc3-ucp-picker-slot">{picker}</div>}
        {note}
      </div>
    </header>
  );
}

/**
 * The segmented picker beside a use case's title: a small label, then a row of pills, on one line. `multiple` is the
 * one difference between the two uses: record levels are a radio group (Accounts / Contacts / Both),
 * calculation methods are checkboxes that add, and the last one on cannot be turned off.
 */
function OptionPicker({
  id,
  title,
  options,
  multiple,
  isOn,
  onPick,
}: {
  id: string;
  title: string;
  options: { id: string; label: string }[];
  multiple: boolean;
  isOn: (optionId: string) => boolean;
  onPick: (optionId: string) => void;
}) {
  return (
    <div className="vc3-ucp-picker">
      <span id={id} className="vc3-ucp-picker-label">
        {title}
      </span>
      <div className="vc3-seg" role={multiple ? 'group' : 'radiogroup'} aria-labelledby={id}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            role={multiple ? 'checkbox' : 'radio'}
            aria-checked={isOn(o.id)}
            className="vc3-seg-opt"
            data-option={o.id}
            onClick={() => onPick(o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Notes under the title: the picker's own note, then the use case's authored explainer as side-by-side oat cards, one per term. */
function PickerNotes({ note, levelNote }: { note: string | null; levelNote: SizedUseCase['levelNote'] | null }) {
  if (!note && !levelNote?.length) return null;
  return (
    <div className="vc3-ucp-picker-notes">
      {note && (
        <p className="vc3-note" role="status">
          {note}
        </p>
      )}
      {levelNote?.length ? (
        <div className="vc3-level-notes">
          {levelNote.map((l) => (
            <p key={l.term} className="vc3-note vc3-level-note">
              <strong>{l.term}</strong> {l.text}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RecordLevels({
  uc,
  levels,
  onChange,
}: {
  uc: SizedUseCase;
  levels: RecordLevel[];
  onChange: (levels: RecordLevel[]) => void;
}) {
  const current = levelChoiceOf(levels);
  return (
    <OptionPicker
      id={`vc3-lv-${uc.id}`}
      title={`Which type of records are being ${uc.levelVerb ?? 'enriched/sourced'}?`}
      options={LEVEL_CHOICES}
      multiple={false}
      isOn={(o) => current === o}
      onPick={(o) => onChange(LEVEL_CHOICES.find((c) => c.id === o)?.levels ?? levels)}
    />
  );
}

const METHODS_QUESTION = 'How do you measure time saved?';

/**
 * First visit to a use case with a mode choice: ask it before the inputs, as one card per choice with
 * its authored description. Record levels pick on click; methods toggle (at least one stays on) and
 * commit on Continue. Once stored, the page opens on its inputs and the header picker changes it.
 */
function EntryChoice({
  uc,
  kind,
  onLevels,
  onMethods,
}: {
  uc: SizedUseCase;
  kind: 'levels' | 'methods';
  onLevels: (levels: RecordLevel[]) => void;
  onMethods: (methods: string[]) => void;
}) {
  const [picked, setPicked] = useState<string[]>(() => uc.methods.slice(0, 1).map((m) => m.id));
  const cards: { id: string; eyebrow: string | null; label: string; text: string | null }[] =
    kind === 'levels'
      ? LEVEL_CHOICES.map((c) => ({ id: c.id, eyebrow: null, label: c.label, text: uc.levelChoiceNote?.[c.id] ?? null }))
      : uc.methods.map((m) => ({
          id: m.id,
          eyebrow: `Method ${methodLetter(uc, m.id)}`,
          label: m.label,
          text: m.description ?? null,
        }));
  const pick = (id: string) => {
    if (kind === 'levels') {
      const choice = LEVEL_CHOICES.find((c) => c.id === id);
      if (choice) onLevels(choice.levels);
      return;
    }
    const next = picked.includes(id) ? picked.filter((m) => m !== id) : [...picked, id];
    if (next.length > 0) setPicked(next);
  };
  return (
    <article className="vc3-ucp" aria-label={uc.name}>
      <Header uc={uc} picker={null} note={null} />
      <section className="vc3-entry" aria-labelledby="vc3-entry-h">
        <h2 id="vc3-entry-h" className="vc3-ucp-h2">
          {kind === 'levels' ? `Which type of records are being ${uc.levelVerb ?? 'enriched/sourced'}?` : METHODS_QUESTION}
        </h2>
        <p className="vc3-note">
          {kind === 'levels'
            ? 'Pick one to start. You can change it at the top of the page later.'
            : 'Pick one or more. Each method’s hours add together; you can change this later.'}
        </p>
        <div
          className="vc3-entry-cards"
          role={kind === 'levels' ? 'radiogroup' : 'group'}
          aria-labelledby="vc3-entry-h"
        >
          {cards.map((c) => (
            <button
              key={c.id}
              type="button"
              role={kind === 'levels' ? 'radio' : 'checkbox'}
              aria-checked={kind === 'levels' ? false : picked.includes(c.id)}
              className="vc3-entry-card"
              data-option={c.id}
              onClick={() => pick(c.id)}
            >
              {c.eyebrow && <span className="vc3-entry-eyebrow">{c.eyebrow}</span>}
              <span className="vc3-entry-label">{c.label}</span>
              {c.text && <span className="vc3-entry-text">{c.text}</span>}
              {kind === 'methods' && (
                <span className="vc3-entry-check" aria-hidden="true">
                  {picked.includes(c.id) && <Ph name="check" />}
                </span>
              )}
            </button>
          ))}
        </div>
        {kind === 'methods' && (
          <div className="vc3-entry-actions">
            <button type="button" className="vc3-btn" onClick={() => onMethods(picked)}>
              Continue
            </button>
          </div>
        )}
      </section>
    </article>
  );
}

const methodsNote = (methods: string[]): string | null =>
  methods.length > 1 ? 'Each method’s hours add together; the hourly rate is applied once to the total.' : null;

/** GTM automation: pick any mix of the time-savings methods; their hours add, then the rate applies once. */
function Methods({
  uc,
  methods,
  onChange,
}: {
  uc: SizedUseCase;
  methods: string[];
  onChange: (methods: string[]) => void;
}) {
  return (
    <OptionPicker
      id={`vc3-mt-${uc.id}`}
      title="How do you measure time saved?"
      options={uc.methods.map((m) => ({ id: m.id, label: m.label }))}
      multiple
      isOn={(o) => methods.includes(o)}
      // At least one stays on: switching the last one off is a no-op, as the store would restore it.
      onPick={(o) => {
        const next = methods.includes(o) ? methods.filter((m) => m !== o) : [...methods, o];
        if (next.length > 0) onChange(next);
      }}
    />
  );
}

/** The five card colours, as a flat row of chips under the chain's lede. */
// Financial value covers two card colours (program value lime, Clay-attributed slushie), so it shows both.
const LEGEND: { id: string; label: string; swatches?: number }[] = [
  { id: 'input', label: 'Your input' },
  { id: 'assumed', label: 'Assumed' },
  { id: 'deal', label: 'Deal economics' },
  { id: 'calculated', label: 'Calculated' },
  { id: 'financial', label: 'Financial value', swatches: 2 },
];

function ChainLegend() {
  return (
    <ul className="vc3-legend" aria-label="Card colors">
      {LEGEND.map((l) => (
        <li key={l.id} className="vc3-legend-item" data-kind={l.id}>
          {Array.from({ length: l.swatches ?? 1 }, (_, i) => (
            <span key={i} className="vc3-legend-swatch" data-swatch={i} aria-hidden />
          ))}
          {l.label}
        </li>
      ))}
    </ul>
  );
}

const ARROW_R = 10;
/** The pulse dots are 9px and the caret takes the last ~12px of the track. */
const DOT = 9;
const CARET_ROOM = 14;

/** Orthogonal branch from a card's right edge to the join point, rounded where it turns. */
function branchPath(ax: number, ay: number, mx: number, my: number): string {
  if (Math.abs(ay - my) < 1) return `M ${ax} ${ay} H ${mx}`;
  const dir = my > ay ? 1 : -1;
  const r = Math.min(ARROW_R, Math.abs(my - ay) / 2, Math.max(0, (mx - ax) / 2));
  return `M ${ax} ${ay} H ${mx - r} Q ${mx} ${ay} ${mx} ${ay + dir * r} V ${my - dir * r} Q ${mx} ${my} ${mx + r} ${my}`;
}

interface ArrowGeom {
  /** Solid bracket lines from each leading card's right edge to the join (empty for one card). */
  branches: string[];
  /** Where the pulse arrow starts, and its width, relative to the strip. */
  x: number;
  y: number;
  width: number;
}

/**
 * v2's blue "drives" pulse arrow, from the leading metric card(s) into the Clay-attributed tile: a
 * 3px blueberry track, three dots running along it, a bold caret and a mono DRIVES label. With two
 * cards, solid bracket lines join their right edges into a trunk and the pulse starts at the join.
 * Measured from the DOM so it follows the cards; static track + caret under prefers-reduced-motion.
 */
function SummaryArrow({ container }: { container: HTMLElement | null }) {
  const [geom, setGeom] = useState<ArrowGeom | null>(null);
  useEffect(() => {
    const el = container;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const box = el.getBoundingClientRect();
      const cards = [...el.querySelectorAll<HTMLElement>('[data-metric]')].map((c) => c.getBoundingClientRect());
      const tile = el.querySelector<HTMLElement>('[data-tile]')?.getBoundingClientRect();
      if (!cards.length || !tile) return setGeom(null);
      const ax = Math.max(...cards.map((c) => c.right)) - box.left;
      const tx = tile.left - box.left - 4;
      const ty = tile.top + tile.height / 2 - box.top;
      // One card: the pulse starts at its edge. Two: they join a trunk part-way across the gap.
      const mx = cards.length > 1 ? ax + (tx - ax) * 0.3 : ax;
      const branches =
        cards.length > 1
          ? cards.map((c) => branchPath(c.right - box.left, c.top + c.height / 2 - box.top, mx, ty))
          : [];
      const next: ArrowGeom = { branches, x: mx, y: ty, width: Math.max(0, tx - mx) };
      setGeom((g) => (g && JSON.stringify(g) === JSON.stringify(next) ? g : next));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.querySelectorAll('[data-metric], [data-tile]').forEach((n) => ro.observe(n));
    return () => ro.disconnect();
  }, [container]);
  return (
    <div className="vc3-sumarrow" aria-hidden>
      {geom && (
        <>
          {geom.branches.length > 0 && (
            <svg className="vc3-sumarrow-brackets" focusable="false">
              <g fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                {geom.branches.map((d) => (
                  <path key={d} d={d} />
                ))}
              </g>
            </svg>
          )}
          {geom.width > 24 && (
            <div className="vc3-pulse" style={{ left: geom.x, top: geom.y, width: geom.width }}>
              <div
                className="vc3-pulse-track"
                style={{ '--run': `${Math.max(0, geom.width - CARET_ROOM - DOT)}px` } as CSSProperties}
              >
                <span className="vc3-pulse-line" />
                {[0, 0.8, 1.6].map((d) => (
                  <span key={d} className="vc3-pulse-dot" style={{ animationDelay: `${d}s` }} />
                ))}
                <Ph name="caret-right" bold className="vc3-pulse-caret" />
              </div>
              <span className="vc3-pulse-label">drives</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Leading metric(s) today -> with Clay on the left, one animated arrow, one tile holding program value and Clay-attributed. */
function SummaryStrip({
  uc,
  result,
  realizationPct,
  attributionPct,
}: {
  uc: SizedUseCase;
  result: UseCaseResult;
  realizationPct: number;
  attributionPct: number;
}) {
  const hero = heroVm(uc, result);
  const tile = summaryTileVm(uc, result.value, realizationPct, attributionPct);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  return (
    <div className="vc3-sumstrip" ref={setContainer}>
      <SummaryArrow container={container} />
      <div className="vc3-sumstrip-lead">
        {hero.leading.map((m) => (
          <div key={m.key} className="vc3-metric" data-metric>
            <div className="vc3-metric-top">
              <span className="vc3-metric-name">
                {m.name}
                {m.levelLabel && <span className="vc3-metric-level"> · {m.levelLabel}</span>}
              </span>
              <span className="vc3-metric-tag">{m.measurement}</span>
            </div>
            <div className="vc3-metric-move">
              <span className="vc3-metric-val">{m.today}</span>
              {m.withClay !== null && (
                <>
                  <span className="vc3-metric-arrow" aria-label="to">
                    →
                  </span>
                  <span className="vc3-metric-val vc3-metric-val-clay">{m.withClay}</span>
                </>
              )}
              {m.unitNote && <span className="vc3-metric-unit">{m.unitNote}</span>}
            </div>
            <div className="vc3-metric-sub">
              {m.withClay !== null ? 'today → with Clay' : 'as entered'}
              {m.assumed && <span className="vc3-assumed">Assumed</span>}
            </div>
          </div>
        ))}
      </div>
      <div className="vc3-sumstrip-gap" aria-hidden />
      {/* One target for the DRIVES arrow: program value over Clay-attributed, both at full emphasis. */}
      <div className="vc3-sumtiles" data-tile>
        <dl className="vc3-sumtile" data-tone="program">
          <dt>{tile.program.label}</dt>
          <dd>{tile.program.text}</dd>
        </dl>
        <dl className="vc3-sumtile" data-tone="attributed">
          <dt>{tile.attributed.label}</dt>
          <dd>{tile.attributed.text}</dd>
        </dl>
      </div>
    </div>
  );
}

function Affix({ kind, children }: { kind: QuestionVm['kind']; children: ReactNode }) {
  if (kind === 'money' || kind === 'rate') return <div className="vc3-affix">{children}</div>;
  return <div className="vc3-affix vc3-affix-pct">{children}</div>;
}

const AFFIX_TEXT: Record<string, string> = { money: '$', rate: '$', pct: '%', hrs: 'hrs', min: 'min' };

function QuestionField({
  ucId,
  q,
  editor,
  recheck,
}: {
  ucId: string;
  q: QuestionVm;
  editor: AnswerEditor | null;
  /** Why migration flagged this answer, when it did. */
  recheck?: string;
}) {
  const id = `vc3-q-${ucId}-${q.key}`;
  const text = editor?.texts[q.key] ?? '';
  const invalid = text.trim() !== '' && parseNumberInput(text) === null;
  const affix = AFFIX_TEXT[q.kind];
  return (
    <div className="vc3-field">
      <label className="vc3-label" htmlFor={id}>
        {q.label}
        {q.level && <span className="vc3-metric-level"> · {LEVEL_LABEL[q.level]}</span>}
        {recheck && (
          <span className="vc3-recheck" title={recheck}>
            Re-check
          </span>
        )}
      </label>
      {editor ? (
        <>
          <Affix kind={q.kind}>
            <NumberField
              id={id}
              className="vc3-input"
              {...AUTOFILL_IGNORE_PROPS}
              aria-invalid={invalid}
              value={text}
              placeholder={q.defText.replace(/^\$|%$| hrs$| min$/g, '')}
              onChange={(t) => editor.onChange(q, t)}
            />
            {affix && <span aria-hidden>{affix}</span>}
          </Affix>
          <span className="vc3-help">
            {q.assumed ? (
              <>
                <span className="vc3-assumed">Assumed</span> Clay default {q.defText}
              </>
            ) : (
              <>
                Clay default {q.defText}{' '}
                <button type="button" className="vc3-linkbtn" onClick={() => editor.onReset(q)}>
                  Reset
                </button>
              </>
            )}
          </span>
        </>
      ) : (
        <div className="vc3-readonly-val" id={id}>
          {q.defText}
        </div>
      )}
    </div>
  );
}

export function Inputs({
  uc,
  groups,
  editor,
  onResetAll,
  anyAnswered,
  flagged,
}: {
  uc: SizedUseCase;
  groups: ReturnType<typeof questionGroups>;
  editor: AnswerEditor | null;
  onResetAll?: () => void;
  anyAnswered: boolean;
  /** question key -> why migration flagged it (the review list passes this). */
  flagged?: Record<string, string>;
}) {
  return (
    <section className="vc3-ucp-block" aria-labelledby={`vc3-inputs-h-${uc.id}`}>
      <div className="vc3-ucp-h2-row">
        <h2 id={`vc3-inputs-h-${uc.id}`} className="vc3-ucp-h2">
          Inputs
        </h2>
        {editor && anyAnswered && onResetAll && (
          <button type="button" className="vc3-linkbtn" onClick={onResetAll}>
            Reset all to Clay defaults
          </button>
        )}
      </div>
      {editor && (
        <p className="vc3-help vc3-ucp-inputs-lede">
          Leave an input blank to use Clay’s default. Defaults are marked Assumed until you give a number.
        </p>
      )}
      <div className="vc3-groups">
        {groups.map((g) => (
          <fieldset key={g.role} className="vc3-group" data-role={g.role}>
            <legend>
              <span className="vc3-group-title">{g.title}</span>
              <span className="vc3-group-blurb">{g.blurb}</span>
            </legend>
            <div className="vc3-form">
              {g.questions.map((q) => (
                <QuestionField key={q.key} ucId={uc.id} q={q} editor={editor} recheck={flagged?.[q.key]} />
              ))}
            </div>
          </fieldset>
        ))}
      </div>
    </section>
  );
}

export function FactorField({
  id,
  label,
  defPct,
  rationale,
  editor,
  readOnlyPct,
}: {
  id: string;
  label: string;
  defPct: number;
  rationale: string;
  editor: FactorEditor | null;
  readOnlyPct: number;
}) {
  return (
    <div className="vc3-field">
      <label className="vc3-label" htmlFor={id}>
        {label}
      </label>
      {editor ? (
        <div className="vc3-affix vc3-affix-pct" style={{ maxWidth: 160 }}>
          <NumberField
            id={id}
            className="vc3-input"
            {...AUTOFILL_IGNORE_PROPS}
            value={editor.text}
            placeholder={String(defPct)}
            onChange={editor.onChange}
          />
          <span aria-hidden>%</span>
        </div>
      ) : (
        <div className="vc3-readonly-val" id={id}>
          {readOnlyPct}%
        </div>
      )}
      <span className="vc3-help vc3-rationale">
        {rationale}
        {editor && !editor.isDefault && (
          <>
            {' '}
            <button type="button" className="vc3-linkbtn" onClick={editor.onReset}>
              Use default ({defPct}%)
            </button>
          </>
        )}
      </span>
    </div>
  );
}


/* ----------------------------------------------------------------------- the page */

export interface PageViewProps {
  uc: SizedUseCase;
  result: UseCaseResult;
  levels: RecordLevel[];
  /** Calculation methods on (empty for use cases without any). */
  methods: string[];
  groups: ReturnType<typeof questionGroups>;
  rows: ChainRowVm[];
  /** The realization / attribution section's rows. */
  raRows: ChainRowVm[];
  realizationPct: number;
  attributionPct: number;
  overlaps: { otherName: string; note: string }[];
  editing: {
    onLevels: (levels: RecordLevel[]) => void;
    onMethods: (methods: string[]) => void;
    answers: AnswerEditor;
    onResetAll: () => void;
    anyAnswered: boolean;
    realization: FactorEditor;
    attribution: FactorEditor;
    /** Tool consolidation's checklist replaces the questions. */
    inputsSlot: ReactNode | null;
  };
  /** Real page: the deal cards' "Edit" link. */
  onEditDeal?: () => void;
}

function UseCasePageView(p: PageViewProps) {
  const { uc, editing } = p;
  const questions = useMemo(
    () => Object.fromEntries(p.groups.flatMap((g) => g.questions).map((q) => [q.key, q])),
    [p.groups],
  );
  const chainCtx = {
    ucId: uc.id,
    questions,
    answers: editing.answers,
    realization: editing.realization,
    attribution: editing.attribution,
    onEditDeal: p.onEditDeal ?? null,
  };
  return (
    <article className="vc3-ucp" aria-label={uc.name}>
      <Header
        uc={uc}
        picker={
          uc.recordLevels.length > 1 ? (
            <RecordLevels uc={uc} levels={p.levels} onChange={editing.onLevels} />
          ) : uc.methods.length > 1 ? (
            <Methods uc={uc} methods={p.methods} onChange={editing.onMethods} />
          ) : null
        }
        note={
          <PickerNotes
            note={uc.methods.length > 1 ? methodsNote(p.methods) : null}
            levelNote={uc.recordLevels.length > 1 ? uc.levelNote : null}
          />
        }
      />
      {editing.inputsSlot ? (
        // Tool consolidation: the checklist is the input, so there is no "How the number is built"
        // chain; realization and attribution start from its "Tool spend removed" node.
        <section className="vc3-ucp-block" aria-labelledby="vc3-inputs-h">
          <h2 id="vc3-inputs-h" className="vc3-ucp-h2">
            Tools Clay replaces
          </h2>
          {editing.inputsSlot}
        </section>
      ) : (
        <section className="vc3-ucp-block" aria-labelledby="vc3-formula-h">
          <div className="vc3-ucp-h2-row">
            <div className="vc3-ucp-h2-lead">
              <h2 id="vc3-formula-h" className="vc3-ucp-h2">
                How the number is built:
              </h2>
              <ChainLegend />
            </div>
            {editing.anyAnswered && (
              <button type="button" className="vc3-linkbtn" onClick={editing.onResetAll}>
                Reset all to Clay defaults
              </button>
            )}
          </div>
          <FormulaChain rows={p.rows} {...chainCtx} />
        </section>
      )}
      <hr className="vc3-ucp-divider" />
      <section className="vc3-ucp-block" aria-labelledby="vc3-ra-h">
        <h2 id="vc3-ra-h" className="vc3-ucp-h2">
          Realization and attribution
        </h2>
        <p className="vc3-help vc3-ucp-inputs-lede">
          Not all of the value is realized, and not all of it is Clay’s. Edit either percentage to match your business.
        </p>
        <FormulaChain rows={p.raRows} {...chainCtx} />
      </section>
      {p.overlaps.map((o) => (
        <div key={o.otherName} className="vc3-banner" role="note">
          <Ph name="info" size={16} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            <strong>Overlaps with {o.otherName}.</strong> {o.note}
          </span>
        </div>
      ))}
      <hr className="vc3-ucp-divider" />
      <section className="vc3-ucp-block" aria-labelledby="vc3-sum-h">
        <h2 id="vc3-sum-h" className="vc3-ucp-h2">
          Summary
        </h2>
        <SummaryStrip uc={uc} result={p.result} realizationPct={p.realizationPct} attributionPct={p.attributionPct} />
      </section>
    </article>
  );
}

/* ---------------------------------------------------------- editor source + hook */

interface PageDraft {
  answers: Record<string, string>;
  realization: string;
  attribution: string;
}

const pctText = (v: number | '' | null | undefined, def: number) => (v == null || v === '' ? String(def) : String(v));

/** Where a use case page reads its sizing from and writes its edits to. */
export interface EditorSource {
  /** Bumped when the sizing is replaced wholesale; reseeds the draft. */
  epoch: number;
  sel: UseCaseSelection | undefined;
  deal: DealEconomics;
  tools: Record<string, number | ''> | null;
  customTools: CustomTool[] | null;
  /** Ids of every selected use case, joined (overlap notes). */
  selectedIds: string;
  actions: {
    setRecordLevels: (id: string, levels: RecordLevel[]) => void;
    setMethods: (id: string, methods: string[]) => void;
    setAnswer: (id: string, key: string, value: number | '') => void;
    resetAnswer: (id: string, key: string) => void;
    resetAnswers: (id: string) => void;
    setRealization: (id: string, value: number | '' | null) => void;
    setAttribution: (id: string, value: number | '' | null) => void;
  };
  /** The Tool consolidation checklist reads the store, so only the real page offers it. */
  withToolChecklist: boolean;
}

/** Everything a use case editing surface needs: derived values plus the draft-backed editors. */
export interface UseCaseEditor {
  uc: SizedUseCase;
  sel: UseCaseSelection;
  result: UseCaseResult;
  levels: RecordLevel[];
  methods: string[];
  groups: ReturnType<typeof questionGroups>;
  rows: ChainRowVm[];
  raRows: ChainRowVm[];
  realizationPct: number;
  attributionPct: number;
  overlaps: { otherName: string; note: string }[];
  draftKey: string;
  editing: NonNullable<PageViewProps['editing']>;
}

function useEditorFromSource(useCaseId: string, src: EditorSource): UseCaseEditor | null {
  const uc = getSizedUseCase(useCaseId);
  const { epoch, sel, deal, tools, customTools, selectedIds, actions } = src;

  const seed: PageDraft = {
    answers: Object.fromEntries(Object.entries(sel?.answers ?? {}).map(([k, v]) => [k, numberToInput(v === '' ? null : v)])),
    realization: pctText(sel?.realization, uc?.realization.pct ?? 0),
    attribution: pctText(sel?.attribution, uc?.attribution.pct ?? 0),
  };
  const draftKey = `${epoch}:${useCaseId}`;
  const { draft, setDraft } = useDraftState<PageDraft>(seed, draftKey);
  const d = draft ?? seed;

  const result = useMemo(
    () => (sel ? computeUseCase(sel, deal, { tools, customTools }) : null),
    [sel, deal, tools, customTools],
  );

  if (!uc || !sel || !result) return null;

  const levels = enabledLevels(uc, sel.recordLevels);
  const methods = enabledMethods(uc, sel.methods);
  const groups = questionGroups(uc, levels, sel.answers, deal.acv, methods);
  const realizationPct = resolveFactor(sel.realization, uc.realization.pct);
  const attributionPct = resolveFactor(sel.attribution, uc.attribution.pct);
  const overlaps = overlapNotesFor(uc.id, selectedIds.split('|')).map((o) => {
    const other = o.between.find((x) => x !== uc.id) ?? '';
    return { otherName: getSizedUseCase(other)?.name ?? other, note: o.note };
  });
  const chainInput = { uc, result, levels, answers: sel.answers, dealAcv: deal.acv, realizationPct, attributionPct };
  const rows = buildChain(chainInput);
  const raRows = buildRealizationRows(rows, chainInput);

  const factor = (
    which: 'realization' | 'attribution',
    def: number,
    setter: (id: string, v: number | '' | null) => void,
  ): FactorEditor => {
    const stored = sel[which];
    return {
      text: d[which],
      pct: which === 'realization' ? realizationPct : attributionPct,
      isDefault: stored == null || stored === '',
      onChange: (text) => {
        setDraft({ [which]: text });
        const n = parseNumberInput(text);
        setter(uc.id, text.trim() === '' ? null : n === null ? stored ?? null : Math.min(n, 100));
      },
      onReset: () => {
        setDraft({ [which]: String(def) });
        setter(uc.id, null);
      },
    };
  };

  return {
    uc,
    sel,
    result,
    levels,
    methods,
    groups,
    rows,
    raRows,
    realizationPct,
    attributionPct,
    overlaps,
    draftKey,
    editing: {
      onLevels: (next) => actions.setRecordLevels(uc.id, next),
      onMethods: (next) => actions.setMethods(uc.id, next),
      answers: {
        texts: d.answers,
        onChange: (q, text) => {
          setDraft({ answers: { ...d.answers, [q.key]: text } });
          if (text.trim() === '') actions.resetAnswer(uc.id, q.key);
          else {
            const n = parseNumberInput(text);
            if (n !== null) actions.setAnswer(uc.id, q.key, n);
          }
        },
        onReset: (q) => {
          const { [q.key]: _drop, ...rest } = d.answers;
          setDraft({ answers: rest });
          actions.resetAnswer(uc.id, q.key);
        },
      },
      onResetAll: () => {
        setDraft({ answers: {} });
        actions.resetAnswers(uc.id);
      },
      anyAnswered: Object.keys(sel.answers ?? {}).length > 0,
      realization: factor('realization', uc.realization.pct, actions.setRealization),
      attribution: factor('attribution', uc.attribution.pct, actions.setAttribution),
      inputsSlot: src.withToolChecklist && uc.id === 'uc-tool-consolidation' ? <UseCaseTools draftKey={draftKey} /> : null,
    },
  };
}

/**
 * The store-connected editor behind a use case page, shared with the review list so both edit
 * through the same re-render-safe draft (seeded once per store `epoch` + use case id) and the same
 * store actions. Null when the use case is not part of the sizing.
 */
export function useUseCaseEditor(useCaseId: string): UseCaseEditor | null {
  const epoch = useCalculatorStore((s) => s.epoch);
  const sel = useCalculatorStore((s) => s.selected.find((x) => x.id === useCaseId));
  const deal = useCalculatorStore((s) => s.deal);
  const tools = useCalculatorStore((s) => s.tools);
  const customTools = useCalculatorStore((s) => s.customTools);
  const selectedIds = useCalculatorStore((s) => s.selected.map((x) => x.id).join('|'));
  return useEditorFromSource(useCaseId, {
    epoch,
    sel,
    deal,
    tools,
    customTools,
    selectedIds,
    actions: calculatorActions,
    withToolChecklist: true,
  });
}

export function UseCaseStep({ useCaseId }: { useCaseId: string }) {
  const e = useUseCaseEditor(useCaseId);
  if (!e) return <p className="vc3-banner vc3-banner-error">This use case is not part of the sizing.</p>;
  const entry = needsEntryChoice(e.uc, e.sel);
  if (entry)
    return <EntryChoice uc={e.uc} kind={entry} onLevels={e.editing.onLevels} onMethods={e.editing.onMethods} />;
  return <UseCasePageView {...e} onEditDeal={() => calculatorActions.goToStep(DEAL_STEP)} />;
}
